import { t } from 'i18next';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { useTranslation } from 'react-i18next';
import { formatAsDate, getFormattedToday } from '@/utils/format';

dayjs.extend(isBetween);

export const calculateDDay = (targetDate: string) => {
  const currentDate = new Date();
  const target = new Date(targetDate);
  const timeDiff = target.getTime() - currentDate.getTime();
  const dDay = Math.ceil(timeDiff / (1000 * 3600 * 24));
  switch (dDay) {
    case 0:
      return 'D-Day';
    default: {
      const result = Math.abs(dDay);
      return dDay > 0 ? `D-${result}` : `D+${result}`;
    }
  }
};

export const formatBirthday = (date?: string) => {
  if (date) {
    const [month, day] = date.split('-');
    return `${t(`${month}월`)} ${t(`${day}일`)}`;
  } else {
    return '-';
  }
};

export const isAvailableDate = (startDateStr: string, endDateStr?: string) => {
  // 시작일이 없으면 false 반환
  if (!startDateStr) return false;

  const now = dayjs();

  // 종료일이 있는 경우: 현재가 시작일과 종료일 사이에 있는지 확인 (시작 경계만 포함)
  if (endDateStr) {
    // '()' : 시작과 끝 모두 포함하지 않음 (strict)
    // '[)' : 시작은 포함, 끝은 포함하지 않음
    // '()' : 시작은 포함하지 않음, 끝은 포함하지 않음
    // '[]' : 시작과 끝 모두 포함 (inclusive)
    return now.isBetween(startDateStr, endDateStr, null, '[)');
  }

  // 종료일이 없는 경우: 현재가 시작일과 같거나 이후인지 확인
  return !now.isBefore(startDateStr);
};

export const getIsWeekend = (date = dayjs()) => {
  const day = date.day(); // 일:0, 월:1, ... 토:6
  return day === 0 || day === 6;
};

export const getWeekMonFri = (today = dayjs()) => {
  // 오늘이 주말인지 체크
  const isWeekend = getIsWeekend(today);

  // 기준일 지정: 주말이면 다음 주 월요일 기준으로 계산
  const baseDate = isWeekend ? today.add(1, 'week') : today;

  // 이번 주 또는 다음 주 월요일: day()===1 (월요일)까지 날짜 조정
  // dayjs.weekDay 플러그인이 있으면 더 편하지만 기본 day() 사용 시 아래와 같이 조정
  const mon = baseDate.subtract((baseDate.day() + 6) % 7, 'day'); // 월요일로 이동
  // 금요일은 월요일 기준 +4일
  const fri = mon.add(4, 'day');

  return {
    monday: formatAsDate(mon),
    friday: formatAsDate(fri),
  };
};

export const getDateRangeList = (
  startDateStr: string,
  endDateStr: string,
): string[] => {
  const startDate = dayjs(startDateStr);
  const endDate = dayjs(endDateStr);

  if (!startDate.isValid() || !endDate.isValid()) {
    throw new Error('Invalid date format');
  }

  if (endDate.isBefore(startDate)) {
    throw new Error('endDate must be same or after startDate');
  }

  const dates: string[] = [];
  let current = startDate;

  while (
    current.isSame(startDate) ||
    current.isBetween(startDate, endDate, null, '[]')
  ) {
    dates.push(formatAsDate(current));
    current = current.add(1, 'day');
  }

  return dates;
};

export const getIsAllDay = (
  startDateTime: string,
  endDateTime: string,
): boolean => {
  const start = dayjs(startDateTime);
  const end = dayjs(endDateTime);

  // 시간이 모두 00:00:00 인지 체크 (하루종일일 경우 대개 이렇게 설정됨)
  const isStartMidnight =
    start.hour() === 0 && start.minute() === 0 && start.second() === 0;
  const isEndMidnight =
    end.hour() === 0 && end.minute() === 0 && end.second() === 0;

  // 날짜 차이 (일 단위)
  const dayDiff = end.startOf('day').diff(start.startOf('day'), 'day');

  return isStartMidnight && isEndMidnight && dayDiff >= 1;
};

export const getIsOngoing = (
  startDateTime: string,
  endDateTime: string,
  timeZone = 'UTC',
  minOffset = 30,
) => {
  const start = dayjs.tz(startDateTime, timeZone);
  const end = dayjs.tz(endDateTime, timeZone);
  const now = dayjs().tz(timeZone);

  // 시작 시간 전 minOffset 이내인지
  let withinMin = false;
  if (minOffset) {
    withinMin = start.isAfter(now) && start.diff(now, 'minute') <= minOffset;
  }
  // 진행중인지
  const ongoing = now.isAfter(start) && now.isBefore(end);

  return withinMin || ongoing;
};

export const getPeriodList = () => {
  const now = getFormattedToday();
  const { t } = useTranslation();

  return [
    {
      name: `${t('최근')} 30${t('일')}`,
      type: 0,
      value: formatAsDate(dayjs().subtract(30, 'day')),
      value2: now,
    },
    {
      name: `${t('최근')} 180${t('일')}`,
      type: 1,
      value: formatAsDate(dayjs().subtract(180, 'day')),
      value2: now,
    },
    {
      name: `${t('최근')} 365${t('일')}`,
      type: 2,
      value: formatAsDate(dayjs().subtract(1, 'year')),
      value2: now,
    },
    {
      name: t('직접 선택'),
      type: 3,
      value: formatAsDate(dayjs().subtract(1, 'day')),
      value2: now,
    },
  ];
};

/**
 * 기준일을 기준으로 일정 범위의 날짜 구간을 반환합니다.
 *
 * @param baseDate 기준 날짜 (기본값: 오늘)
 * @param days 포함할 일 수 (예: 7 → 7일 구간)
 * @param direction 기준일 기준 'before' | 'after'
 * @param inclusiveEnd 종료일 포함 여부 (기본값: true)
 * @returns [startDate, endDate]
 */
export function getDateRange(
  baseDate: Date = new Date(),
  days: number,
  direction: 'before' | 'after' = 'before',
  inclusiveEnd = true,
): [Date, Date] {
  const base = dayjs(baseDate).startOf('day'); // 기준일 00시

  if (direction === 'before') {
    const end = (inclusiveEnd ? base : base.subtract(1, 'day')).endOf('day'); // ✅ 끝은 23:59:59
    const start = end.subtract(days - 1, 'day').startOf('day'); // ✅ 시작은 00:00:00
    return [start.toDate(), end.toDate()];
  }

  // direction === 'after'
  const start = (inclusiveEnd ? base : base.add(1, 'day')).startOf('day');
  const end = start.add(days - 1, 'day').endOf('day');
  return [start.toDate(), end.toDate()];
}
